import api from './api';

/*
 * The AI certainty thresholds (app_settings 'ai.thresholds', edited by collection
 * managers and internal experts on the review page) and the rules that read them.
 *
 *   high       top-1 >= high_certainty_min and the answer was NOT routed to expert
 *              review: offers one-click accept, unless a candidate is dangerous
 *   suggested  top-1 >= suggest_min
 *   uncertain  everything else
 *
 * Nothing here approves anything. "Accept" only fills in the caller's form; a
 * person still submits it.
 */
export const DEFAULT_THRESHOLDS = Object.freeze({
  suggest_min: 0.7,
  high_certainty_min: 0.9,
  game_queue_levels: [0.3, 0.5, 0.7],
});

const inUnit = v => typeof v === 'number' && v > 0 && v <= 1;

export function normalizeThresholds(raw) {
  const t = raw || {};
  const suggest = inUnit(t.suggest_min) ? t.suggest_min : DEFAULT_THRESHOLDS.suggest_min;
  const high = inUnit(t.high_certainty_min) && t.high_certainty_min > suggest
    ? t.high_certainty_min : Math.max(DEFAULT_THRESHOLDS.high_certainty_min, suggest);
  const levels = Array.isArray(t.game_queue_levels) && t.game_queue_levels.length > 0
    && t.game_queue_levels.every(inUnit) ? t.game_queue_levels : DEFAULT_THRESHOLDS.game_queue_levels;
  return { suggest_min: suggest, high_certainty_min: high, game_queue_levels: levels };
}

let cached = null;

/** {thresholds, measured, can_edit, updated_at}. Never rejects: an older backend
 * without the route, or no connection, gives the defaults. */
export function loadAiSettings({ refresh = false } = {}) {
  if (cached && !refresh) return cached;
  let request;
  try {
    request = api.get('/settings/ai-thresholds');
  } catch {
    request = null;
  }
  cached = Promise.resolve(request)
    .then(res => {
      const data = res && res.data;
      return {
        thresholds: normalizeThresholds(data && data.thresholds),
        measured: (data && data.measured) || null,
        can_edit: Boolean(data && data.can_edit),
        updated_at: (data && data.updated_at) || null,
        from_server: Boolean(data && data.thresholds),
      };
    })
    .catch(() => ({ thresholds: normalizeThresholds(null), measured: null, can_edit: false,
                    updated_at: null, from_server: false }));
  return cached;
}

export function forgetAiSettings() {
  cached = null;
}

export function certaintyOf(result, thresholds) {
  const t = normalizeThresholds(thresholds);
  const top = result && result.predictions && result.predictions[0];
  if (!top || typeof top.confidence !== 'number') return 'uncertain';
  const routed = Boolean(result.review && result.review.needs_review);
  if (!routed && top.confidence >= t.high_certainty_min) return 'high';
  if (top.confidence >= t.suggest_min) return 'suggested';
  return 'uncertain';
}

/** Why one-click accept must not be offered, or null. Any toxicity flag, any
 * dangerous look-alike, anything flagged below the displayed list, or a
 * deadly/severe severity anywhere blocks it: the person then types the name
 * themselves, having read the warning. */
export function acceptBlockedReason(result) {
  if (!result) return 'noResult';
  const safety = result.safety || {};
  const predictions = result.predictions || [];
  const severities = [
    safety.max_severity,
    ...(safety.tail || []).map(x => x.max_severity),
    ...predictions.map(p => p.safety && p.safety.max_severity),
  ];
  const flagged = safety.toxicity_flag || safety.high_risk || (safety.tail || []).length > 0
    || predictions.some(p => p.toxicity_flag || (p.safety && (
      (p.safety.flags || []).length || (p.safety.same_genus || []).length || (p.safety.lookalike_of || []).length)));
  if (flagged || severities.some(s => s === 'deadly' || s === 'severe')) return 'dangerous';
  return null;
}
